import * as React from 'react'
import { Row, Icon, Input, message, Result, Skeleton } from 'antd'
import Text from 'antd/lib/typography/Text'
import {
  ILocationWithMetaDataContract,
  ICompanyWithMetaDataContract,
  BirchmountStatus,
  MetaDataType,
} from '@getgreenline/homi-shared'
import { observer, inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { BMConfigStore } from './BirchmountConfigStore'
import { debounce } from 'lodash'
import { BirchmountLocationTable } from './BirchmountLocationTable'
import { ButtonGroup } from '../../../../../components/Button/ButtonGroup'
import { HelpLinks } from '../../../../../constants/ExternalLinks'

interface Props {
  companyWithMetaData: ICompanyWithMetaDataContract<MetaDataType.STRING>
  locationsWithMetaData: Array<ILocationWithMetaDataContract<MetaDataType.INTEGER>>
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

@inject('currentCompanyStore')
@observer
export class BirchmountConfig extends React.Component<Props & InjectedProps> {
  DEBOUNCE_TIME = 500

  API_MESSAGE_KEY = 'apiMessage'

  MESSAGE_DURATION_IN_SEC = 2

  companyId = this.props.currentCompanyStore!.company!.id

  bmConfigStore = new BMConfigStore(this.props)

  componentDidMount = async () => {
    await this.loadLocationContract()
  }

  loadLocationContract = async () => {
    this.bmConfigStore.setIsLocationContractLoading(true)
    await this.bmConfigStore.getLocationContract(this.bmConfigStore.apiKey)
    this.bmConfigStore.setIsLocationContractLoading(false)
  }

  debounceGetLocationContract = debounce(async (apiKey: string | null) => {
    try {
      if (apiKey === null) {
        return this.syncApiKey()
      }

      const locationContract = await this.bmConfigStore.getLocationContract(apiKey)

      if (locationContract?.STATUS === BirchmountStatus.SUCCESS) {
        await this.syncApiKey()
      }
    } catch (error) {
      message.error({
        content: 'Please enter a valid API key and try again',
        key: this.API_MESSAGE_KEY,
        duration: this.MESSAGE_DURATION_IN_SEC,
      })
    } finally {
      this.bmConfigStore.setIsLocationContractLoading(false)
    }
  }, this.DEBOUNCE_TIME)

  syncApiKey = async () => {
    try {
      this.bmConfigStore.setIsApiKeyUpdating(true)

      await this.bmConfigStore.saveCompanyMetaData(this.companyId)

      if (this.bmConfigStore.apiKey === null) {
        await this.bmConfigStore.unsyncAllBirchmountIds(this.companyId)
      }

      const successMessage =
        this.bmConfigStore.apiKey === null
          ? 'Birchmount API key removed'
          : `Birchmount API key updated to ${this.bmConfigStore.apiKey}`

      message.success({
        content: successMessage,
        key: this.API_MESSAGE_KEY,
        duration: this.MESSAGE_DURATION_IN_SEC,
      })
    } catch (error) {
      message.error({
        content: parseErrorMsg(error || `An error occurred while updating Birchmount API key`),
        key: this.API_MESSAGE_KEY,
        duration: this.MESSAGE_DURATION_IN_SEC,
      })
    } finally {
      this.bmConfigStore.setIsApiKeyUpdating(false)
    }
  }

  onApiInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const apiKey = this.bmConfigStore.setApiKey(e.target.value || null)

    this.bmConfigStore.setIsLocationContractLoading(true)

    await this.debounceGetLocationContract(apiKey)

    if (apiKey === null) {
      this.bmConfigStore.setLocationContract(undefined)
    }
  }

  get apiKeyCaption(): JSX.Element | undefined {
    const { apiKey, isLocationContractLoading, locationContract } = this.bmConfigStore

    if (!apiKey) {
      return <span className='caption'>Valid API key required to set Location IDs</span>
    }

    if (isLocationContractLoading) {
      return <span className='caption'>Validating API key. Please wait...</span>
    }

    if (locationContract === undefined) {
      return
    }

    if (locationContract.STATUS === BirchmountStatus.SUCCESS) {
      return <span className='caption text-success'>Your API key is valid and saved</span>
    }

    if (locationContract.STATUS === BirchmountStatus.FAILURE) {
      return <span className='caption text-danger'>Your API key is invalid</span>
    }
  }

  render() {
    const {
      canSaveApiKey,
      isApiKeyEditable,
      isApiKeyUpdating,
      setIsApiKeyEditable,
      revertApiKey,
      apiKey,
    } = this.bmConfigStore

    return (
      <Row>
        <Row className='mb-4'>
          <Icon className='mr-2' type='question-circle' />
          <Text>
            Setup instructions can be found{' '}
            <a href={HelpLinks.BIRCHMOUNT_GIFT_CARDS} target='_blank' rel='noreferrer'>
              here
            </a>
            .
          </Text>
        </Row>

        <Row className='mb-4'>
          <Input
            addonBefore={<b>API key</b>}
            addonAfter={
              <ButtonGroup
                canSave={canSaveApiKey}
                editable={isApiKeyEditable}
                isUpdating={isApiKeyUpdating}
                onSave={this.syncApiKey}
                onEdit={() => setIsApiKeyEditable(true)}
                onCancel={async () => {
                  revertApiKey()
                  if (apiKey) {
                    await this.loadLocationContract()
                  }
                  setIsApiKeyEditable(false)
                }}
              />
            }
            value={apiKey || undefined}
            disabled={!isApiKeyEditable}
            onChange={this.onApiInputChange}
            onPressEnter={() => setIsApiKeyEditable(false)}
          />

          {this.apiKeyCaption}
        </Row>

        <Row className='mb-4'>
          {this.bmConfigStore.apiKey !== null &&
          this.bmConfigStore.locationContract?.STATUS === BirchmountStatus.SUCCESS ? (
            this.bmConfigStore.isLocationContractLoading ? (
              <div>
                <Skeleton active className='p-4' />
                <Skeleton active className='p-4' />
              </div>
            ) : (
              <BirchmountLocationTable bmConfigStore={this.bmConfigStore} />
            )
          ) : (
            <Result
              status='403'
              title='Invalid API key'
              subTitle='Please use valid API key first'
            />
          )}
        </Row>
      </Row>
    )
  }
}
